import { useState } from 'react'
import {
  Input,
  Select,
  renderIcon,
  kuiperClassNames,
  WithTooltip,
  Tabs,
  Button,
} from '@opallabs/kuiper'
import type { SectionOptionProps } from '../types'
import { styles } from './styles'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

interface Props {
  onChangeSectionOption: (val: string) => void
  sectionOption: SectionOptionProps
  systemScheduledAt: string
  timezone?: string
}

export const ScheduledAtDateInput: React.FC<Props> = ({
  onChangeSectionOption,
  sectionOption,
  systemScheduledAt,
  timezone = 'America/Los_Angeles',
}) => {
  const getFormattedTime = (val = ''): string => {
    // format sectionDate or new time passed from time input
    let hours: number | string | undefined = sectionDate.getHours()
    let minutes: number | string | undefined = sectionDate.getMinutes()
    if (val || isNaN(hours) || isNaN(minutes)) {
      if (val.includes(':')) {
        const timeArr = val.split(':')
        hours = timeArr[0]
        minutes = timeArr[1] || '00'
      } else {
        let fullTime = ''
        // short values should be treated as hours
        if (val.length === 1 || val.length === 2) {
          hours = val
          minutes = '00'
          // longer values should be cropped to fit regular time length
        } else if (val.length > 4) {
          fullTime = val.slice(0, 4)
          hours = fullTime.slice(0, 2)
          minutes = fullTime.slice(2)
          // proper length values should be taken at face value
        } else {
          fullTime = ('0000' + val).slice(-4)
          hours = fullTime.slice(0, 2)
          minutes = fullTime.slice(2)
        }
      }
    }

    minutes = +minutes >= 60 ? 59 : minutes

    // automically set AM/PM based on time input
    if (+hours > 12) {
      hours = +hours >= 24 ? 12 : +hours - 12
      setMeridiem(1)
    }

    // give padding to both sides of the :
    hours = ('00' + hours.toString()).slice(-2)
    minutes = ('00' + minutes.toString()).slice(-2)

    const timeFormat = `${hours}:${minutes}`
    return timeFormat === '00:00' ? '' : `${hours}:${minutes}`
  }

  const getTimezone = (): string | undefined => {
    let timezoneText = ''
    switch (timezone) {
      case 'Pacific/Honolulu':
        timezoneText = 'HST (-10)'
        break
      case 'America/Anchorage':
        timezoneText = 'AKST (-9)'
        break
      case 'America/Los_Angeles':
        timezoneText = 'PST (-8)'
        break
      case 'America/Denver':
        timezoneText = 'MST (-7)'
        break
      case 'America/Chicago':
        timezoneText = 'CST (-6)'
        break
      case 'America/New_York':
        timezoneText = 'EST (-5)'
        break
      case 'America/Argentina/Buenos_Aires':
        timezoneText = 'ART (-3)'
        break
      case 'UTC':
      case 'Etc/UTC':
        timezoneText = 'UTC (+0)'
        break
      case 'Europe/London':
        timezoneText = 'GMT (+0)'
        break
      case 'Europe/Berlin':
        timezoneText = 'CET (+1)'
        break
      case 'Africa/Johannesburg':
        timezoneText = 'SAST (+2)'
        break
      case 'Asia/Baghdad':
        timezoneText = 'AST (+3)'
        break
      case 'Europe/Moscow':
        timezoneText = 'MSK (+3)'
        break
      case 'Asia/Dubai':
        timezoneText = 'GST (+4)'
        break
      case 'Asia/Kolkata':
        timezoneText = 'IST (+5:30)'
        break
      case 'Asia/Almaty':
        timezoneText = 'ALMT (+6)'
        break
      case 'Asia/Jakarta':
        timezoneText = 'WIB (+7)'
        break
      case 'Asia/Hong_Kong':
        timezoneText = 'HKT (+8)'
        break
      case 'Asia/Shanghai':
        timezoneText = 'CST (+8)'
        break
      case 'Asia/Tokyo':
        timezoneText = 'JST (+9)'
        break
      case 'Australia/Brisbane':
        timezoneText = 'AEST (+10)'
        break
      case 'Australia/Sydney':
        timezoneText = 'AEST (+10)'
        break
      case 'Pacific/Auckland':
        timezoneText = 'NZST (+12)'
        break
      default:
        timezoneText = 'PST (-8)'
    }
    return timezoneText
  }
  const validTimezone = timezone || 'America/Los_Angeles'
  const sysDate = new Date(systemScheduledAt)
  const monthName = MONTHS[sysDate.getMonth()]
  const timeZoneTitle = getTimezone()
  const timezoneText = { id: 0, title: timeZoneTitle }
  const dateText = `${monthName} ${sysDate.getDate()}, ${sysDate.getFullYear()}`
  const sectionDate = new Date(sectionOption.content as string)

  const [meridiem, setMeridiem] = useState(0)
  const [time, setTime] = useState(sectionDate ? getFormattedTime : '')

  const onToggleMeridiem = () => {
    const newMeridiem = meridiem === 1 ? 0 : 1
    setMeridiem(newMeridiem)
  }

  const onOptionChange = () => {
    const formattedTime = getFormattedTime(time)
    setTime(formattedTime)

    if (!formattedTime) return
    let hours = +formattedTime.split(':')[0]
    const minutes = +formattedTime.split(':')[1]

    hours = meridiem === 1 ? hours + 12 : hours

    const newDateTime = new Date(
      sysDate.getFullYear(),
      sysDate.getMonth(),
      sysDate.getDay(),
      hours,
      minutes
    ).toISOString()
    onChangeSectionOption(newDateTime)
  }

  const handleInputChange = (val: string) => {
    const newVal = val.replace(':', '')
    if (isNaN(+newVal)) return
    setTime(val)
  }

  return (
    <div css={styles.datePickerWrapper} onMouseLeave={() => onOptionChange()}>
      <div css={styles.calendarIcon}>
        <WithTooltip
          content={`Content start dates are inherited from moment start dates: ${dateText}`}
          tooltipOptions={{ position: 'top' }}
        >
          {renderIcon('calendar', {
            style: { height: 14, width: 14 },
            ariaLabel: `Start Date: ${dateText}`,
            ariaMode: 'presentational',
          })}
        </WithTooltip>
      </div>
      <div css={styles.timeInput}>
        <Input
          tabIndex={0}
          ariaLabel="Section Scheduled At Time Text"
          theme="light"
          className={kuiperClassNames(
            'modular-section-scheduled-at-option-time'
          )}
          type="text"
          value={time}
          placeholder="HH:MM"
          name="Scheduled At Time"
          onBlur={onOptionChange}
          onChange={(val: string): void => handleInputChange(val)}
        />
      </div>
      <div
        css={styles.ampmToggleInputWrapper}
        className={kuiperClassNames('modular-section-scheduled-at-option-ampm')}
      >
        <Tabs
          name="meridiemSwitch"
          activeTab={meridiem}
          theme="switch"
          onChange={onToggleMeridiem}
        >
          <Button
            tabIndex={0}
            ariaLabel="Section Scheduled At AM Toggle Button"
            label="am"
            name="AM"
            type="invisible"
          />
          <Button
            tabIndex={0}
            ariaLabel="Section Scheduled At PM Toggle Button"
            label="pm"
            name="PM"
            type="invisible"
          />
        </Tabs>
      </div>
      <div>
        <WithTooltip
          tooltipOptions={{ position: 'top' }}
          content={`Scheduled at timezones are inherited from content timezones: ${validTimezone}`}
        >
          <Select
            ariaLabel="Section Scheduled At Timezone Dropdown"
            theme="light"
            className={kuiperClassNames(
              'modular-section-scheduled-at-option-timezone'
            )}
            options={[timezoneText]}
            disabled={true}
            placeholder="Choose..."
            value={timezoneText}
          />
        </WithTooltip>
      </div>
    </div>
  )
}
